import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ApiService } from './services/api.service';
import { Diploma, StudentModel } from './models/StudentModel';
import { CommonModule } from '@angular/common';
import { SearchCriteria } from './models/SearchCriteria';
import { FormsModule, NgForm } from '@angular/forms';
import { LibWebVetResponse } from './models/LibWebVetResponse';
import { forkJoin, Subscription } from 'rxjs';
import { AccountService } from './services/account.service';
import { AssistanceRequest } from './models/AssistanceRequest';
import { environment } from '../environments/environment';
import { version } from '../../package.json'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {

  //Enviornment and version
  appEnvironment = environment.env;
  appVersion = version;

  subscription: Subscription = new Subscription;
  isUserAuthenticated = false;

  title = 'assistance-form-ui';
  student: StudentModel | undefined;
  isStudentAllowed: boolean | null = null;
  userNotAllowedMessage: string = "";
  message: string = '';

  criteria: SearchCriteria = {
    codAnu: '',
    codDip: '',
    codVrsVdi: '',
    codEtp: '',
    codVrsVet: '',
    codElp: ''
  };

  // Prepare an AssistanceRequest object with default empty values.
  assistanceRequest: AssistanceRequest = {
    nickname: '',
    studentNumber: '',
    email: '',
    messageContent: '',
    codesEtape: [],
    conversationCodesEtape: []
  };

  // Flag to indicate that the diploma checkboxes have been interacted with
  diplomasTouched: boolean = false;

  // Flag to indicate that the studentRequest checkboxes have been interacted with
  studentRequestTouched: boolean = false;

  successMessage: string | null = null;
  errorMessage: string | null = null;

  response: LibWebVetResponse | null = null;

  constructor(private apiService: ApiService, private accountService: AccountService) {
    this.apiService.checkAuthentication().subscribe();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription = this.accountService.isUserAuthenticated.subscribe(isAuthenticated => {
      this.isUserAuthenticated = isAuthenticated;
    });

    this.hasAllowedCursus();
  }

  login() {
    this.apiService.login();
  }

  hasAllowedCursus() {
    this.apiService.checkIfStudentHasAllowedCursus().subscribe(
      {
        next: response => {
          this.isStudentAllowed = response.isAllowed;
          this.userNotAllowedMessage = response.errorMessage;
          if (response.isAllowed) { this.getStudentDetails(); }
        },
        error: error => this.message = 'Error: ' + error.message
      }
    );
  }

  getStudentDetails() {
    this.apiService.getDetails().subscribe(
      {
        next: response => {
          this.student = response;
          this.updateDiplomasWithLibWebVet();
          if (this.student.diplomas) {
            this.student.diplomas.forEach((diploma: Diploma) => {
              diploma.selected = false;
            });
          }
          // If there's exactly one diploma, mark it as selected
          if (this.student.diplomas && this.student.diplomas.length === 1) {
            this.student.diplomas[0].selected = true;
          }
        },
        error: error => this.message = 'Error: ' + error.message
      }
    );
  }

  getAuth() {
    this.apiService.checkAuthentication().subscribe({
      next: res => this.isUserAuthenticated = res
    });
  }

  get studentName(): string {
    return `${this.student?.givenName || ''} ${this.student?.sn || ''}`;
  }

  // For each diploma, call the backend to get its libWebVet value.
  updateDiplomasWithLibWebVet(): void {
    if (this.student?.diplomas && this.student.diplomas.length > 0) {
      const diplomaRequests = this.student.diplomas.map((diploma: Diploma) => {
        return this.apiService.getLibWebVet(this.student?.supannEtuAnneeInscription || '', diploma.codeEtape || '', diploma.versionEtape || '');
      });

      // forkJoin waits for all HTTP calls to complete
      forkJoin(diplomaRequests).subscribe(results => {
        // Update each diploma with its corresponding libWebVet value.
        this.student!.diplomas!.forEach((diploma, index) => {
          diploma.libWebVet = results[index].libWebVet;
          if (diploma.selected === undefined) {
            diploma.selected = false;
          }
        });
      });
    }
  }


  // Utility function to check if at least one diploma checkbox is selected
  isAnyDiplomaSelected(): boolean {
    return this.student?.diplomas ? this.student.diplomas.some(d => d.selected) : false;
  }

  // Mark that the diploma checkboxes have been touched
  markDiplomaTouched(): void {
    this.diplomasTouched = true;
  }

  // Mark that the studentRequest checkboxes have been touched
  markStudentRequestTouched(): void {
    this.studentRequestTouched = true;
  }

  onSubmit(form: NgForm): void {
    // Clear existing conversationCodeEtapeList
    this.assistanceRequest.conversationCodesEtape = [];
    // Clear existing codeEtapeList
    this.assistanceRequest.codesEtape = [];
    // For each diploma in the student, if it's selected, add its codeEtape to the list.
    if (this.student?.diplomas) {
      this.student.diplomas.forEach((diploma: Diploma) => {
        if (diploma.selected && diploma.codeEtape) {
          this.assistanceRequest.conversationCodesEtape.push(diploma.codeEtape);
        }
        diploma.codeEtape && this.assistanceRequest.codesEtape.push(diploma.codeEtape.concat('-', diploma.versionEtape || '', ' : ', diploma.libWebVet || ''));
      });
    }
    this.assistanceRequest.nickname = `${this.student?.givenName || ''} ${this.student?.sn || ''}`.trim();
    this.assistanceRequest.studentNumber = this.student?.studentNumber || '';
    this.assistanceRequest.email = this.student?.email || '';

    // Send the request to the backend.
    if (form.valid && this.isAnyDiplomaSelected()) {
      this.apiService.sendAssistanceRequest(this.assistanceRequest).subscribe({
        next: response => {
          // Success Message
          this.successMessage = `Votre demande a été envoyée avec succès.`;
          this.resetForm(form);
        },
        error: error => {
          // Error Message
          this.errorMessage = 'Une erreur est survenue, merci de réessayer ultérieurement.';
          this.resetForm(form);
        }
      });
    }
  }

  resetForm(form: NgForm): void {
    // Reset diplomas selection
    if (this.student?.diplomas) {
      this.student.diplomas.forEach(diploma => {
        diploma.selected = false; // Uncheck all checkboxes
      });

      // If there's only one diploma, keep it checked
      if (this.student.diplomas.length === 1) {
        this.student.diplomas[0].selected = true;
      }
    }

    // Reset the "Demande" textarea field
    this.assistanceRequest.messageContent = '';
    // Reset studentRequestTouched flag to hide validation messages
    this.studentRequestTouched = false;

    // Reset diplomasTouched flag to hide validation messages
    this.diplomasTouched = false;
  }

  // Called when the user clicks the close button on the success alert.
  closeAlert(): void {
    this.successMessage = null;
  }

  // Similarly for error alert.
  closeErrorAlert(): void {
    this.errorMessage = null;
  }

}
